import { ApolloError } from "@apollo/client";
import { XMarkIcon } from "@heroicons/react/24/outline";
import moment from "moment-timezone";
import { Button, ButtonGroup, Label, Link, Text } from "north.js";
import React, { useMemo, useState } from "react";
import styled from "styled-components";

import { PageWrapper } from "src/shared/components";
import Header from "src/shared/components/Header";
import { APP_ENV } from "src/shared/utils/env";
import { QR } from "src/static/img";
import { mediaQueries } from "src/theme/deviceBreakpoints";

import { useCheckIntoShiftMutation } from "../shared/components/VolunteerShiftModal/graphql/checkIntoShift.generated";
import { withBuffer } from "../shared/utils/buffer";

import MixedResultBottomSheet from "./components/MixedResultBottomSheet";
import MockTimeControl from "./components/MockTimeControl";
import QRScanner from "./components/QRScanner";
import ResultBottomSheet from "./components/ResultBottomSheet";
import ShiftRow from "./components/ShiftRow";
import ShiftSection from "./components/ShiftSection";
import {
  TAssignedShift,
  TCheckInResult,
  TShift,
  TVolunteer,
} from "./constants";
import { useVolunteerAssignedShiftsQuery } from "./graphql/volunteerAssignedShifts.generated";
import { useMockableNow } from "./hooks/useMockableNow";
import {
  formatHoursCollected,
  getEligibility,
  getRelativeTime,
  getVolunteerScanStatus,
} from "./utils";

const CheckInPage = () => {
  const { now, mockNow, setMockNow } = useMockableNow();

  const [volunteer, setVolunteer] = useState<TVolunteer | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [manualInput, setManualInput] = useState(false);
  const [selectedShiftIds, setSelectedShiftIds] = useState<number[]>([]);
  const [locallyCheckedInIds, setLocallyCheckedInIds] = useState<number[]>([]);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [checkInResults, setCheckInResults] = useState<TCheckInResult[]>([]);
  const [resultShifts, setResultShifts] = useState<TShift[]>([]);
  const [mixedResults, setMixedResults] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);

  const [checkIntoShift] = useCheckIntoShiftMutation();

  const {
    data,
    loading,
    error: assignedShiftsError,
    refetch,
  } = useVolunteerAssignedShiftsQuery({
    variables: { event_attendee_id: volunteer?.eventAttendeeId ?? 0 },
    skip: !volunteer,
    fetchPolicy: "network-only",
  });

  const assignedShifts: TAssignedShift[] = useMemo(() => {
    const signups = (data?.volunteerShiftSignups ?? []).filter(
      (signup) => signup.shift.start_time && signup.shift.end_time
    );

    const buffered = withBuffer(
      signups.map((signup) => ({
        shiftId: signup.shift_id,
        start: new Date(signup.shift.start_time),
        end: new Date(signup.shift.end_time),
        checkedIn:
          signup.checked_in || locallyCheckedInIds.includes(signup.shift_id),
      }))
    );
    const bufferedById = new Map(
      buffered.map((shift) => [shift.shiftId, shift])
    );

    return signups
      .map((signup) => {
        const bufferedShift = bufferedById.get(signup.shift_id)!;
        const shift: TShift = {
          id: signup.shift_id,
          name: signup.shift.name,
          startTime: bufferedShift.start,
          endTime: bufferedShift.end,
          displayEndTime: bufferedShift.effectiveEnd,
          hasBuffer: bufferedShift.hasBuffer,
        };
        return {
          ...shift,
          eligibility: getEligibility(
            now,
            shift,
            signup.checked_in || locallyCheckedInIds.includes(signup.shift_id)
          ),
        };
      })
      .sort(
        (a, b) => moment(a.startTime).valueOf() - moment(b.startTime).valueOf()
      );
  }, [data, locallyCheckedInIds, now]);

  const scanStatus = useMemo(
    () => getVolunteerScanStatus(now, assignedShifts),
    [assignedShifts, now]
  );

  const legalNameLabel = useMemo(() => {
    if (!volunteer) return null;
    const legal = volunteer.legalName.trim();
    if (!legal || legal.toLowerCase() === volunteer.name.trim().toLowerCase()) {
      return null;
    }
    return legal;
  }, [volunteer]);

  const selectedShifts = useMemo(
    () =>
      assignedShifts.filter(
        (shift) =>
          shift.eligibility === "eligible" &&
          selectedShiftIds.includes(shift.id)
      ),
    [assignedShifts, selectedShiftIds]
  );

  // Upcoming counts up from now (soonest first); the past sections count back
  // from now (most recent first), so the rows nearest to "now" are always on top.
  const groupedShifts = useMemo(() => {
    const byStart = (a: TAssignedShift, b: TAssignedShift) =>
      moment(a.startTime).valueOf() - moment(b.startTime).valueOf();
    const group = (eligibility: TAssignedShift["eligibility"]) =>
      assignedShifts.filter((shift) => shift.eligibility === eligibility);

    return {
      upcoming: group("eligible").sort(byStart),
      checkedIn: group("checked-in").sort((a, b) => byStart(b, a)),
      missed: group("too-late").sort((a, b) => byStart(b, a)),
    };
  }, [assignedShifts]);

  const closeResults = () => {
    setShowResultModal(false);
    setCheckInResults([]);
    setResultShifts([]);
    setMixedResults(false);
  };

  const openScanner = (useManualInput: boolean) => {
    setScanError(null);
    setManualInput(useManualInput);
    setShowCameraModal(true);
  };

  const handleVolunteerFound = (found: TVolunteer) => {
    closeResults();
    setScanError(null);
    setSelectedShiftIds([]);
    setLocallyCheckedInIds([]);
    setVolunteer(found);
  };

  const handleScanError = (message: string) => {
    closeResults();
    setVolunteer(null);
    setSelectedShiftIds([]);
    setLocallyCheckedInIds([]);
    setScanError(message);
  };

  const handleScanAnother = () => {
    closeResults();
    setVolunteer(null);
    setSelectedShiftIds([]);
    setLocallyCheckedInIds([]);
    openScanner(false);
  };

  const handleToggleShift = (shiftId: number) => {
    setSelectedShiftIds((prev) =>
      prev.includes(shiftId)
        ? prev.filter((id) => id !== shiftId)
        : [...prev, shiftId]
    );
  };

  const handleCheckIn = async () => {
    if (isCheckingIn || !volunteer || selectedShifts.length === 0) return;

    const shifts = selectedShifts;
    setIsCheckingIn(true);

    const results: TCheckInResult[] = [];
    for (const shift of shifts) {
      try {
        const response = await checkIntoShift({
          variables: {
            shift_id: shift.id,
            event_attendee_id: volunteer.eventAttendeeId,
          },
        });
        results.push({ success: true, data: response.data });
      } catch (e) {
        results.push({ success: false, error: e as ApolloError });
      }
    }

    const successfulShiftIds = shifts
      .filter((_, index) => results[index].success)
      .map((shift) => shift.id);
    setLocallyCheckedInIds((previousIds) => [
      ...previousIds,
      ...successfulShiftIds.filter((id) => !previousIds.includes(id)),
    ]);
    setSelectedShiftIds((previousIds) =>
      previousIds.filter((id) => !successfulShiftIds.includes(id))
    );

    const allSuccess = results.every((r) => r.success);
    const allFailure = results.every((r) => !r.success);
    const uniqueErrorMessages = new Set(
      results.filter((r) => !r.success).map((r) => r.error?.message)
    );

    setMixedResults(
      (!allSuccess && !allFailure) ||
        (allFailure && uniqueErrorMessages.size > 1)
    );
    setResultShifts(shifts);
    setCheckInResults(results);
    setShowResultModal(true);

    try {
      await refetch();
      setLocallyCheckedInIds([]);
    } catch {
      // The results sheet already reports what happened for each shift.
    } finally {
      setIsCheckingIn(false);
    }
  };

  const retryAssignedShifts = async () => {
    try {
      await refetch();
    } catch {
      // Apollo updates assignedShiftsError, which keeps the retry state visible.
    }
  };

  return (
    <>
      <Header
        title="Volunteer Scan"
        subtitle="Scan a volunteer to see their info and shifts"
      />
      <PageWrapper fullWidth>
        {showResultModal &&
          checkInResults.length > 0 &&
          (mixedResults ? (
            <MixedResultBottomSheet
              isOpen={showResultModal}
              shifts={resultShifts}
              shiftResults={checkInResults}
              onScanAnother={handleScanAnother}
              onClose={closeResults}
            />
          ) : (
            <ResultBottomSheet
              isOpen={showResultModal}
              volunteerName={volunteer?.name}
              shifts={resultShifts}
              shiftStatus={checkInResults[0]}
              onScanAnother={handleScanAnother}
              onClose={closeResults}
            />
          ))}

        <QRScanner
          showCameraModal={showCameraModal}
          setShowCameraModal={setShowCameraModal}
          manualInput={manualInput}
          setManualInput={setManualInput}
          onVolunteerFound={handleVolunteerFound}
          onScanError={handleScanError}
        />

        {(APP_ENV === "development" || APP_ENV === "staging") && (
          <MockTimeControl
            now={now}
            mockNow={mockNow}
            setMockNow={setMockNow}
          />
        )}

        {volunteer ? (
          <>
            <VolunteerHeader>
              <VolunteerInfo>
                <VolunteerLine>
                  <VolunteerName type="h2">
                    {volunteer.name}
                    {legalNameLabel ? (
                      <LegalName> ({legalNameLabel})</LegalName>
                    ) : null}
                  </VolunteerName>
                  {!loading && !assignedShiftsError && (
                    <StatusPill color={scanStatus.color} tw="font-bold">
                      {scanStatus.label}
                    </StatusPill>
                  )}
                </VolunteerLine>
                <Text type="body2">
                  {[
                    volunteer.email,
                    volunteer.primaryRole,
                    !loading && !assignedShiftsError
                      ? formatHoursCollected(scanStatus.hours)
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </Text>
                {volunteer.notes ? (
                  <VolunteerNotes type="body2">
                    {volunteer.notes}
                  </VolunteerNotes>
                ) : null}
              </VolunteerInfo>
              <Button color="primary" size="md" onClick={handleScanAnother}>
                Scan another volunteer
              </Button>
            </VolunteerHeader>
            {!loading && !assignedShiftsError && assignedShifts.length > 0 && (
              <SelectHint type="body2">
                Select the shifts to check them into.
              </SelectHint>
            )}
            {loading && <Text>Loading their shifts...</Text>}
            {!loading && assignedShiftsError && (
              <ShiftLoadError>
                <ErrorText>
                  Could not load this volunteer&apos;s shifts. Please try again.
                </ErrorText>
                <Button
                  color="secondary"
                  size="sm"
                  onClick={retryAssignedShifts}
                >
                  Retry
                </Button>
              </ShiftLoadError>
            )}
            {!loading &&
              !assignedShiftsError &&
              assignedShifts.length === 0 && (
                <Text>This volunteer is not signed up for any shifts.</Text>
              )}
            {!loading && !assignedShiftsError && assignedShifts.length > 0 && (
              <>
                <ShiftSection
                  title="Upcoming"
                  count={groupedShifts.upcoming.length}
                  defaultOpen
                >
                  {groupedShifts.upcoming.map((shift) => (
                    <ShiftRow
                      key={shift.id}
                      shiftName={shift.name}
                      startTime={shift.startTime}
                      endTime={shift.displayEndTime}
                      relativeTime={getRelativeTime(now, shift)}
                      selectable
                      checked={selectedShifts.some((s) => s.id === shift.id)}
                      onToggle={() => handleToggleShift(shift.id)}
                    />
                  ))}
                </ShiftSection>
                <ShiftSection
                  title="Checked in"
                  count={groupedShifts.checkedIn.length}
                >
                  {groupedShifts.checkedIn.map((shift) => (
                    <ShiftRow
                      key={shift.id}
                      shiftName={shift.name}
                      startTime={shift.startTime}
                      endTime={shift.displayEndTime}
                      relativeTime={getRelativeTime(now, shift)}
                      selectable={false}
                      checked
                      onToggle={() => undefined}
                    />
                  ))}
                </ShiftSection>
                <ShiftSection
                  title="Too late / Incomplete"
                  count={groupedShifts.missed.length}
                >
                  {groupedShifts.missed.map((shift) => (
                    <ShiftRow
                      key={shift.id}
                      shiftName={shift.name}
                      startTime={shift.startTime}
                      endTime={shift.displayEndTime}
                      relativeTime={getRelativeTime(now, shift)}
                      selectable={false}
                      checked={false}
                      onToggle={() => undefined}
                    />
                  ))}
                </ShiftSection>
              </>
            )}
            {selectedShifts.length > 0 && (
              <ActionDrawer>
                <ButtonGroup>
                  <Button>{selectedShifts.length} selected</Button>
                  <Button
                    leadingIcon={<XMarkIcon className="w-4 h-4" />}
                    color="secondary-danger"
                    onClick={() => setSelectedShiftIds([])}
                  />
                </ButtonGroup>
                <Button onClick={handleCheckIn} disabled={isCheckingIn}>
                  {isCheckingIn ? "Checking in..." : "Check in"}
                </Button>
              </ActionDrawer>
            )}
          </>
        ) : (
          <ScanPrompt>
            <Text type="h1">Scan a volunteer&apos;s ticket</Text>
            <Text>Their info and assigned shifts will show up here.</Text>
            <QR />
            {scanError && <ErrorText>{scanError}</ErrorText>}
            <Button
              color="primary"
              size="md"
              onClick={() => openScanner(false)}
            >
              Start scanning ➜
            </Button>
            <Link onClick={() => openScanner(true)}>Enter email manually</Link>
          </ScanPrompt>
        )}
      </PageWrapper>
    </>
  );
};

const VolunteerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;

  ${mediaQueries.tablet} {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const VolunteerInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
`;

const VolunteerNotes = styled(Text)`
  white-space: pre-wrap;
`;

const SelectHint = styled(Text)`
  display: block;
  margin-bottom: 8px;
`;

const VolunteerLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const StatusPill = styled(Label)`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  font-weight: 700;
  transform: translateY(2px);
`;

const VolunteerName = styled(Text)`
  word-break: break-word;
  line-height: 1;

  ${mediaQueries.tablet} {
    font-size: 20px;
  }
`;

const LegalName = styled.span`
  font-weight: 400;
`;

const ScanPrompt = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 18px;
  padding: 48px 0;
`;

const ErrorText = styled(Text)`
  color: ${({ theme }) => theme.globalConstants.color.textDanger};
`;

const ShiftLoadError = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionDrawer = styled.div`
  position: sticky;
  display: flex;
  gap: 8px;
  justify-self: center;
  width: fit-content;
  margin-top: 42px;
  bottom: 42px;
  padding: 8px;
  background: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
`;

export default CheckInPage;
